import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, ShieldCheck, AlertTriangle, RefreshCw } from "lucide-react";
import { formatFechaCorta } from "@/lib/agenda";

/**
 * Auditoría de cupos: salidas cuyo cupo_usado no coincide con la ocupación real de sus
 * reservas (PROMPT 65). Solo lectura hasta que se aprieta "Corregir".
 */
export function CuposDesincronizadosDialog({ open, onOpenChange, onDone }) {
  const [loading, setLoading] = useState(false);
  const [corrigiendo, setCorrigiendo] = useState(false);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/salidas/cupos/auditoria");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const corregir = async () => {
    setCorrigiendo(true);
    try {
      const { data } = await api.post("/salidas/cupos/reconciliar");
      toast.success(`${data.corregidas} salida(s) corregida(s) de ${data.revisadas} revisadas.`);
      await load();
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setCorrigiendo(false);
    }
  };

  const filas = data?.desincronizadas || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="cupos-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-slate-700" /> Revisar cupos de las salidas
          </DialogTitle>
          <DialogDescription>
            Compara el cupo usado que muestra cada salida contra los pasajeros reales de sus reservas
            activas. Si alguna quedó corta, el sistema estaría vendiendo lugares ya ocupados; si quedó
            larga, bloquea venta real. Esta pantalla no cambia nada hasta que apretás "Corregir".
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-3 text-sm" data-testid="cupos-resumen">
              <span className="text-slate-600">{data?.revisadas ?? 0} salidas revisadas</span>
              <span className={filas.length ? "text-amber-800 font-medium" : "text-emerald-700 font-medium"}>
                {data?.total_desincronizadas ?? 0} desincronizada(s)
              </span>
              {!!data?.futuras && <span className="text-red-700">{data.futuras} futura(s)</span>}
            </div>

            {filas.length === 0 ? (
              <p className="py-8 text-center text-sm text-emerald-700" data-testid="cupos-ok">
                Todos los cupos coinciden con las reservas reales.
              </p>
            ) : (
              <div className="border rounded-md divide-y max-h-[420px] overflow-y-auto">
                {filas.map((d) => (
                  <div key={d.salida_id} className="px-3 py-2 text-sm flex flex-wrap items-center gap-2"
                       data-testid={`cupo-desync-${d.salida_id}`}>
                    <AlertTriangle className={`h-4 w-4 ${d.futura ? "text-red-600" : "text-slate-400"}`} />
                    <span className="font-medium text-slate-800">{d.tour || "sin tour"}</span>
                    <span className="text-slate-500 font-mono text-xs">
                      {d.fecha ? formatFechaCorta(d.fecha) : "sin fecha"} {d.hora}
                    </span>
                    <span className="text-slate-500 capitalize text-xs">{d.modalidad} · {d.idioma}</span>
                    <span className={`text-xs font-semibold ${d.diferencia > 0 ? "text-red-700" : "text-amber-700"}`}>
                      {d.cupo_usado} → {d.real} de {d.cupo_max}
                    </span>
                    <span className="text-xs text-slate-500">
                      {d.reservas_contadas} reserva(s){d.codigos?.length ? `: ${d.codigos.slice(0, 4).join(", ")}` : ""}
                    </span>
                    {d.cupo_override !== null && d.cupo_override !== undefined && (
                      <span className="text-[11px] italic text-slate-500">cupo editado a mano (se respeta)</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={load} disabled={loading} className="gap-2"
                  data-testid="cupos-refrescar-button">
            <RefreshCw className="h-4 w-4" /> Volver a revisar
          </Button>
          <Button onClick={corregir} disabled={corrigiendo || filas.length === 0} className="gap-2"
                  data-testid="cupos-corregir-button">
            {corrigiendo ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
            Corregir {filas.length || ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CuposDesincronizadosDialog;
